import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react';
import { ReportFilter } from "./ReportFIlter";
import { useFilter } from "../../../../../Components/ListFilter";
import { useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAptorApi } from "../../../../../Api";
import {
  AccessLevel,
  LawPortalFeature,
  Module,
  UserContext
} from "../../../../../Context/UserContext/UserContext";
import moment from "moment";
import ReportTableView from "./ReportTableView";
import { useStyles } from "../../../LawPortal.styles";
import { MTable, Transition } from '../../../../../Components';
import { MTableToolbar } from '@material-table/core';
import { ModuleContext } from '../../../../../Context/ModuleContext/ModuleContext';
import { OldReportFilter } from './OldReportFilter';
import { Alert } from '@mui/material';

type ComplianceReport = {
  id: number;
  requirementId: number;
  requirementName: string;
  effectiveFrom: Date;
  customerCompanyUnitId: number;
  customerCompanyUnit: string;
  userId: number;
  userName: string;
  controlDate: Date;
  unplanned: boolean;
  type?: number;
  reportId: number;
  name?: string;
  companyUnit?: string;
  responsible?: string;
  fulfilment?: number;
};

interface PlanData {
  id: string;
  name: string;
  isDirect: boolean;
  startDate: string;
  endDate: string;
  companyUnit?: string;
  recurrentInterval?: string;
  lawCount?: number;
  executedLawCount?: number;
}

export const ReportsLK = React.memo(() => {
  const [filters, setFilter] = useFilter();
  const [search, setSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { complianceUserFor, hasAccessToFeature, hasAccessToModule, customerId ,customerFeatures, applicationVersion} = useContext(UserContext);
  const isOldVersion = applicationVersion?.value === 1;
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();
  const [isDirectPlan, setIsDirectPlan] = useState<PlanData[]>([]);
  const [isInDirectPlan, setIsInDirectPlan] = useState<PlanData[]>([]);
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<'active' | 'deactive' | 'all'>('active');
  const [allReport, setAllReport] = useState<ComplianceReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { setHeader } = useContext(ModuleContext);

  // Memoize menu configuration
  const menuConfig = useMemo(() => {
   const hasAvvikelserLight = customerFeatures?.some((feature: any) => feature.value === 13);
      const menuKeySuffix = isOldVersion ? '.lek.old' : '.lek';
      let menu: any = [];
      if (hasAccessToModule(Module.Administration) || hasAccessToModule(Module.Organization)) {
        menu = [
          {
            title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.planning${menuKeySuffix}` }),
            to: `/law-portal/legal-compliance-control/planning`,
          },
          {
            title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.carry${menuKeySuffix}` }),
            to: `/law-portal/legal-compliance-control/carry`,
          },
          {
            title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.reports${menuKeySuffix}` }),
            to: `/law-portal/legal-compliance-control/reports`,
          }
        ];
      } else {
        menu = [
          {
            title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.carry${menuKeySuffix}` }),
            to: `/law-portal/legal-compliance-control/carry`,
          },
          {
            title: formatMessage({ id: `law-portal.legal.compliance.sub-menu.reports${menuKeySuffix}` }),
            to: `/law-portal/legal-compliance-control/reports`,
          }
        ];
      }
      if (!isOldVersion && hasAvvikelserLight) {
        menu.push({
          title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.reports.observations' }),
          to: `/law-portal/legal-compliance-control/observations`,
        })
      }

    return menu;
  }, [hasAccessToModule, formatMessage, customerFeatures, isOldVersion]);

  // Set header on mount
  useEffect(() => {
    setHeader(
      formatMessage({
        id: isOldVersion ? 'law-portal.legal.compliance.header' : 'law-portal.legal.compliance.header',
        defaultMessage: isOldVersion ? 'Legal compliance control' : 'Evaluation control',
      }),
      menuConfig
    );
  }, [setHeader, formatMessage, menuConfig]);

  // Parse URL params on mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryParamsMap = {
      tags: 'tags',
      companyUnit: 'companyUnit',
      complianceUsers: 'complianceUsers',
      processes: 'processes',
      aspects: 'aspects',
      category: 'category',
      area: 'area',
    };

    const searchQuery = queryParams.get('search');
    if (searchQuery) {
      setSearch(searchQuery);
    }

    // Object.entries(queryParamsMap).forEach(([queryKey, stateKey]) => {
    //   const queryValue = queryParams.get(queryKey);
    //   if (queryValue) {
    //     setFilter(stateKey, queryValue.split(','));
    //   }
    // });
  }, []);

  // Update URL when filters/search change
  useEffect(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && Array.isArray(value) && value.length > 0) {
        params.set(key, value.join(','));
      }
    });

    if (search) {
      params.set('search', search);
    }

    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    }, { replace: true });
  }, [filters, search, navigate, location.pathname]);

  // Fetch report plans
  const getReportPlan = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.getReportPlan({ filters:{}, search,EffectiveFrom:filters?.EffectiveFrom });
      
      const sortPlans = (plans: PlanData[]) => {
        return plans.sort((a, b) => {
          const startDateComparison = new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
          return startDateComparison === 0 ? a.name.localeCompare(b.name) : startDateComparison;
        });
      };

      const directPlan = sortPlans(response?.plans?.filter((val: PlanData) => val.isDirect) || []);
      const inDirectPlan = sortPlans(response?.plans?.filter((val: PlanData) => !val.isDirect) || []);

      // setIsDirectPlan(directPlan);
      setIsInDirectPlan([...inDirectPlan, ...directPlan]); // Combine direct and indirect plans for display
    } catch (error) {
      console.error('Error fetching report plans:', error);
      setIsDirectPlan([]);
      setIsInDirectPlan([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, filters, search]);

  // Fetch all control reports
  const getAllControlReport = useCallback(async () => {
    try {
      const res:any = await api.getAllControlReport(Number(customerId), {
        filters:{},
        EffectiveFrom:filters?.EffectiveFrom,
        search
      });
      setAllReport(res || []);
    } catch (error) {
      console.error('Error fetching all control reports:', error);
      setAllReport([]);
    }
  }, [api, customerId, filters, search]);

  // Fetch data when filters/search change
  useEffect(() => {
    getReportPlan();
    getAllControlReport();
  }, [getReportPlan, getAllControlReport]);

  // Check download permissions
  const canDownloadAllReports = useMemo(
    () => hasAccessToFeature(LawPortalFeature.Manage, AccessLevel.Manage),
    [hasAccessToFeature]
  );

  const canDownloadReport = useCallback((row: ComplianceReport) => {
    return (
      canDownloadAllReports ||
      complianceUserFor.laws.some(
        (x) => x.customerCompanyUnitId === row.customerCompanyUnitId && x.id === row.id
      )
    );
  }, [canDownloadAllReports, complianceUserFor.laws]);

  const renderDownloadAction = useCallback((row: ComplianceReport) => {
    return {
      tooltip: formatMessage({ id: 'law-portal.statistics.table.download-compliance' }),
      icon: 'picture_as_pdf',
      onClick: () => {
        row.type === 0 
          ? api.downloadLawComplianceReport(row.id, row?.reportId) 
          : api.downloadRequirementComplianceReport(row.id, row?.reportId);
      },
      disabled: !canDownloadReport(row),
    };
  }, [formatMessage, api, canDownloadReport]);

  // Memoize column definitions
  const columnPlan = useMemo(() => [
    { 
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.name-of-control' }), 
      field: 'name' 
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Company-unit' }),
      field: 'companyUnit',
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Start-date' }),
      field: 'startDate',
      render: (row: PlanData) => moment(row?.startDate).format('YYYY-MM-DD'),
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.End-date' }), 
      field: 'endDate',
      render: (row: PlanData) => moment(row?.endDate).format('YYYY-MM-DD'), 
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Recuring-interval' }),
      field: 'recurrentInterval',
      render: (row: PlanData) => row?.recurrentInterval || "-",
      sorting: false,
    },
    {
      title: formatMessage({ id: 'utils.filters.status' }),
      field: 'status',
      render: (row: PlanData) => 
        `${row.executedLawCount} ${formatMessage({ id: 'text.of' })} ${row.lawCount} ${formatMessage({ id: 'status.text' })}`,
      sorting: false,
    },
  ], [formatMessage]);

  // const columnUnplan = useMemo(() => [
  //   { 
  //     title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.name-of-control' }), 
  //     field: 'name' 
  //   },
  //   {
  //     title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Company-unit' }),
  //     field: 'companyUnit',
  //   },
  //   {
  //     title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Start-date' }),
  //     field: 'startDate',
  //     render: (row: PlanData) => moment(row?.startDate).format('YYYY-MM-DD'),
  //     sorting: false,
  //   },
  //   {
  //     title: formatMessage({ id: 'utils.filters.status' }),
  //     field: 'status',
  //     render: (row: PlanData) => 
  //       `${row.executedLawCount} ${formatMessage({ id: 'text.of' })} ${row.lawCount} ${formatMessage({ id: 'status.text' })}`,
  //     sorting: false,
  //   },
  // ], [formatMessage]);

  const allPlanColumns = useMemo(() => [
     {
      title: formatMessage({ id: 'law-portal.our-laws.table.number' }),
      field: 'number',
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.our-laws.table.law' }),
      field: 'name',
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Company-unit' }),
      field: 'companyUnit',
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.statistics.table.control-date' }), 
      field: 'controlDate',
      render: (row: ComplianceReport) => moment(row?.controlDate).format('YYYY-MM-DD'), 
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.execute.fulfilment' }),
      field: 'fulfilment',
      render: (row: ComplianceReport) => <div>{parseFloat(String(row?.fulfilment)).toFixed(1)}%</div>,
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.compliance.report.table.col' }),
      field: 'responsible',
      render: (row: ComplianceReport) => {
        if (row.responsible) {
          const responsibleArr = row.responsible.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {responsibleArr.map((val: string, idx: number) => (
                <span key={idx}>{val}</span>
              ))}
            </div>
          );
        }
        return "-";
      },
      sorting: false
    },
  ], [formatMessage]);

  // Memoize toolbar component
  const ToolbarComponent = useCallback((props: any) => (
    <div className={classes.toolbar} style={{ display: 'flex', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex' }}>
        <div
          className={`${classes.tabsize} ${activeTab === "active" ? classes?.tabActive : classes?.tab}`}
          onClick={() => setActiveTab('active')}
        >
          {/* {formatMessage({ id: "law-portal.compliance.repporter.heading.plan" })} */}
          {formatMessage({ id: 'law-portal.compliance.repporter.complete.plan' })}
          <span className={classes?.tabNumber}>{isInDirectPlan?.length}</span>
        </div>
        {/* <div
          className={`${classes.tabsize} ${activeTab === "deactive" ? classes?.tabActive : classes?.tab}`}
          onClick={() => setActiveTab('deactive')}
        >
          {formatMessage({ id: "law-portal.compliance.repporter.heading.direct" })}
          <span className={classes?.tabNumber}>{isDirectPlan?.length}</span>
        </div> */}
        <div
          className={`${classes.tabsize} ${activeTab === "all" ? classes?.tabActive : classes?.tab}`}
          onClick={() => setActiveTab('all')}
        >
          {formatMessage({ id: 'law-portal.compliance-law.plan-compliance.all-control' })}
          <span className={classes?.tabNumber}>{allReport?.length}</span>
        </div>
      </div>
      <MTableToolbar {...props} />
    </div>
  ), [activeTab, classes, isInDirectPlan, isDirectPlan, allReport, formatMessage]);

  return (
    <div className={classes?.listWrapper}>
      {isOldVersion&&<Alert severity="warning" style={{ marginBottom: '16px' }}>
                    {formatMessage({ id: 'law-portal.profile.applicationVersion.icon.text' })}
                  </Alert>}
     {isOldVersion ? <OldReportFilter 
        filters={filters} 
        setFilter={setFilter} 
        search={search} 
        setSearch={setSearch} 
      /> : <ReportFilter 
        filters={filters} 
        setFilter={setFilter} 
        search={search} 
        setSearch={setSearch} 
      />}
      <hr style={{ marginTop: '25px', marginBottom: "25px" }} />
      
      <Transition isLoading={isLoading}>
        {/* All Controls Table */}
        <div style={{ display: activeTab === 'all' ? 'block' : 'none' }}>
          <MTable
            columns={allPlanColumns}
            data={allReport}
            components={{
              Toolbar: ToolbarComponent
            }}
            actions={[renderDownloadAction]}
            options={{
              pageSizeOptions: [10, 25, 50, 100],
            }}
          />
        </div>

        {/* Active/Planned Controls Table */}
        <div style={{ display: activeTab === 'active' ? 'block' : 'none' }}>
          <MTable
            columns={columnPlan}
            data={isInDirectPlan}
            options={{
              detailPanelColumnAlignment: 'right',
              pageSizeOptions: [10, 25, 50, 100],
            }}
            components={{
              Toolbar: ToolbarComponent
            }}
            detailPanel={[
              {
                tooltip: formatMessage({ id: "text.expend" }),
                render: (rowData: any) => (
                  <ReportTableView 
                    filter={filters} 
                    search={search} 
                    planId={rowData?.rowData.id} 
                    plan={rowData?.rowData} 
                  />
                ),
              }
            ]}
          />
        </div>

        {/* Direct Controls Table */}
        {/* <div style={{ display: activeTab === 'deactive' ? 'block' : 'none' }}>
          <MTable
            columns={columnUnplan}
            data={isDirectPlan}
            options={{
              detailPanelColumnAlignment: 'right',
              pageSizeOptions: [10, 25, 50, 100],
            }}
            components={{
              Toolbar: ToolbarComponent
            }}
            detailPanel={[
              {
                tooltip: formatMessage({ id: "text.expend" }),
                render: (rowData: any) => (
                  <ReportTableView 
                    filter={filters} 
                    search={search} 
                    planId={rowData?.rowData.id} 
                    plan={rowData?.rowData} 
                  />
                ),
              }
            ]}
          />
        </div> */}
      </Transition>
    </div>
  );
});

ReportsLK.displayName = 'ReportsLK';

export default ReportsLK;